import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  MessageSquare,
  CalendarCheck,
  Users,
  Megaphone,
  ClipboardCheck,
  FileText,
  CircleDollarSign,
} from "lucide-react";
const scenes = [
  {
    name: "Get customers",
    icon: Megaphone,
    eyebrow: "Turn attention into an opportunity",
    title: "From your ad. To your next conversation.",
    steps: ["Better message", "New inquiry", "Ready to follow up"],
  },
  {
    name: "Follow up",
    icon: MessageSquare,
    eyebrow: "AI that moves the conversation forward",
    title: "Your AI moves the sale forward.",
    steps: ["AI answers", "AI sends quote", "Customer accepts"],
  },
  {
    name: "Run the work",
    icon: ClipboardCheck,
    eyebrow: "Less copying. Less chasing.",
    title: "One job. Everyone on the same page.",
    steps: ["Quote approved", "Team notified", "Invoice prepared"],
  },
];
export default function VisualDemo() {
  const [active, setActive] = useState(1);
  const [conversationStep, setConversationStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  useEffect(() => {
    if (active !== 1 || !playing || conversationStep >= 4) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setConversationStep(4);
      return;
    }
    const timer = window.setTimeout(() => setConversationStep(step => step + 1), 2100);
    return () => window.clearTimeout(timer);
  }, [active, playing, conversationStep]);
  const scene = scenes[active];
  return (
    <div className="visual-demo">
      <div
        className="demo-tabs"
        role="tablist"
        aria-label="See how AI helps your business"
      >
        {scenes.map((s, i) => (
          <button
            key={s.name}
            role="tab"
            aria-selected={active === i}
            aria-controls="ai-demo-panel"
            id={`ai-demo-tab-${i}`}
            onClick={() => setActive(i)}
          >
            <s.icon size={16} />
            {s.name}
          </button>
        ))}
      </div>
      <div
        className="demo-scene"
        role="tabpanel"
        id="ai-demo-panel"
        aria-labelledby={`ai-demo-tab-${active}`}
      >
        <div className="demo-heading">
          <span className="eyebrow">{scene.eyebrow}</span>
          <h3>{scene.title}</h3>
        </div>
        {active === 0 ? (
          <div className="ad-scene">
            <div className="sample-ad">
              <span className="tiny-label">YOUR BUSINESS · AD CONCEPT</span>
              <div className="ad-art">
                <Megaphone size={44} />
                <span className="floating-chip">
                  <Sparkles size={12} />
                  AI-assisted creative
                </span>
              </div>
              <strong>
                A better offer.
                <br />
                The right next step.
              </strong>
              <div className="fake-button">
                Get a quote <ArrowUpRight size={14} />
              </div>
            </div>
            <div className="mini-lead">
              <div className="mini-icon">
                <Users size={18} />
              </div>
              <div>
                <strong>New inquiry captured</strong>
                <span>Contact + request + source</span>
              </div>
              <Check size={17} />
            </div>
          </div>
        ) : active === 1 ? (
          <div className="quote-app">
            <div className="quote-app-header">
              <span className="quote-avatar ai-agent-avatar">AI</span>
              <div><strong>Your AI sales assistant</strong><span>Helping Jamie · Outdoor lighting</span></div>
              <span className="quote-demo-label">DEMO</span>
            </div>
            <div className="quote-thread autonomous-thread">
              <span className="quote-timestamp">Your AI assistant · Following your business rules</span>
              <p className="quote-bubble incoming">Can you quote the pathway lights we discussed?</p>
              <div className="conversation-progress" aria-live="polite" aria-atomic="false">
                {conversationStep >= 1 && <p className="quote-bubble outgoing"><small>AI ASSISTANT</small>Of course. We discussed 6 or 10 lights. Which option would you like?</p>}
                {conversationStep >= 2 && <p className="quote-bubble incoming">Let’s go with 10.</p>}
                {conversationStep >= 3 && <>
                  <p className="quote-bubble outgoing"><small>AI ASSISTANT</small>Perfect. The 10-light package is $2,400, including installation. I’ve sent the quote for you to review.</p>
                  <div className="quote-attachment">
                    <span className="quote-file-icon"><FileText size={22} /></span>
                    <div><strong>10-light package quote</strong><span>Example: $2,400 · Sent by your AI</span></div>
                    <Check size={18} />
                  </div>
                </>}
                {conversationStep >= 4 && <p className="quote-bubble incoming">Looks good. I’ve accepted the quote.</p>}
                {conversationStep < 4 && <span className="conversation-wait">{conversationStep === 1 || conversationStep === 3 ? "Customer is replying…" : "AI is preparing a reply…"}</span>}
              </div>
            </div>
            <div className="quote-next">
              <CalendarCheck size={19} />
              <div><strong>{conversationStep === 4 ? "Quote accepted. Your team is notified." : "Your knowledge. Your pricing. Your rules."}</strong><span>{conversationStep === 4 ? "AI handled the conversation and updated the customer record." : "Answers routine questions and brings you in when needed."}</span></div>
            </div>
            <div className="quote-app-footer">
              <span>Illustrative conversation · Nothing is sent</span>
              {conversationStep === 4 ? <button type="button" onClick={() => {setConversationStep(0); setPlaying(true);}} aria-label="Replay AI conversation"><RotateCcw size={12} />Replay</button> : <button type="button" onClick={() => setPlaying(!playing)} aria-label={playing ? "Pause AI conversation" : "Play AI conversation"}>{playing ? <Pause size={12} /> : <Play size={12} />}{playing ? "Pause" : "Play"}</button>}
            </div>
          </div>
        ) : (
          <div className="ops-scene">
            <div className="job-card">
              <div className="demo-card-title">
                <strong>Customer project</strong>
                <span className="approved">Approved</span>
              </div>
              <div className="job-task">
                <span>
                  <FileText size={17} />
                  Quote & agreement
                </span>
                <Check size={17} />
              </div>
              <div className="job-task">
                <span>
                  <Users size={17} />
                  Team handoff
                </span>
                <Check size={17} />
              </div>
              <div className="job-task">
                <span>
                  <CalendarCheck size={17} />
                  Schedule
                </span>
                <span className="review-label">Ready to confirm</span>
              </div>
              <div className="job-task">
                <span>
                  <CircleDollarSign size={17} />
                  Invoice
                </span>
                <span className="review-label">Draft prepared</span>
              </div>
            </div>
            <div className="owner-note">
              <Sparkles size={17} />
              <div>
                <strong>Your daily update, ready.</strong>
                <span>See what moved. Know what needs you.</span>
              </div>
            </div>
          </div>
        )}
        <div className="demo-steps">
          {scene.steps.map((s, i) => (
            <div key={s}>
              <span>{i + 1}</span>
              {s}
              {i < 2 && <ArrowRight size={13} />}
            </div>
          ))}
        </div>
      </div>
      <p className="demo-disclaimer">
        Illustrative examples. Automation, pricing rules, and handoffs are configured for your business.
      </p>
    </div>
  );
}
export function OutcomeGraphics() {
  return (
    <div className="outcome-grid">
      {[
        {
          icon: Megaphone,
          title: "Bring in more customers",
          body: "Lead generation, better ads, and a clear path to contact you.",
          steps: ["Ad", "Lead", "Conversation"],
          tone: "sage",
        },
        {
          icon: MessageSquare,
          title: "Follow up without the chasing",
          body: "AI email help, customer replies, reminders, and sales follow-up.",
          steps: ["Inquiry", "AI + follow-up", "Next step"],
          tone: "sand",
        },
        {
          icon: ClipboardCheck,
          title: "Take work off your plate",
          body: "Connect your tools, automate admin, and keep the team moving.",
          steps: ["Customer", "Team", "Work done"],
          tone: "blue",
        },
      ].map((s, index) => (
        <article className={`outcome-card ${s.tone}`} key={s.title}>
          <div className="outcome-art outcome-example" aria-label={`${s.title}: illustrative workflow`}>
            <div className="example-window">
              <div className="example-title"><span>{["MARKETING", "SALES", "OPERATIONS"][index]}</span><span>EXAMPLE</span></div>
              {index === 0 ? <>
                <div className="example-ad"><span>YOUR BUSINESS</span><strong>Ready to transform<br />your outdoor space?</strong><span className="example-cta">Get a free quote <ArrowRight size={12} /></span></div>
                <div className="example-connection"><ArrowRight size={14} /></div>
                <div className="example-result"><span className="example-person">JD</span><div><strong>New customer inquiry</strong><span>Jamie · Wants a lighting quote</span></div><Check size={15} /></div>
              </> : index === 1 ? <>
                <div className="sales-agent-heading"><span>AI</span><div><strong>Your AI sales assistant</strong><small>Answers · Quotes · Follows up</small></div></div>
                <div className="example-chat customer">Can you send the 10-light quote?</div>
                <div className="example-chat assistant"><span>AI REPLY</span>It’s $2,400 installed. Here’s your quote, ready to review.</div>
                <div className="example-chat customer short">Accepted. Let’s do it.</div>
                <div className="example-status sale-complete"><Check size={14} /><div><strong>Customer accepted the quote</strong><span>AI updated the record & notified your team</span></div></div>
              </> : <>
                <div className="example-job"><span>CUSTOMER PROJECT</span><strong>Outdoor lighting installation</strong><span className="example-approved">Quote accepted</span></div>
                <div className="example-task"><Check size={13} /><span>Job added to schedule</span><span>Done</span></div>
                <div className="example-task"><Check size={13} /><span>Crew gets the job details</span><span>Done</span></div>
                <div className="example-task"><Check size={13} /><span>Invoice prepared</span><span>Done</span></div>
                <div className="example-status">One approval. The next steps happen.</div>
              </>}
            </div>
          </div>
          <div className="outcome-copy">
            <h3>{s.title}</h3>
            <p>{s.body}</p>
          </div>
        </article>
      ))}
    </div>
  );
}
